import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, GlobeIcon, BookmarkIcon, PlusIcon, SearchIcon, CheckIcon, XIcon, EditIcon, TrashIcon, SaveIcon } from 'lucide-react';
import { CountryList } from './CountryList';
import { CountryDetail } from './CountryDetail';
export function CountriesManagement({
  locale = 'en-US'
}) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);
  // Sample country data with common and official names
  const [countries, setCountries] = useState([{
    alpha2Code: 'US',
    alpha3Code: 'USA',
    numericCode: '840',
    commonName: 'United States',
    officialName: 'United States of America',
    officialCurrency: 'USD',
    phoneCode: '+1',
    region: 'Americas',
    subRegion: 'Northern America',
    customFields: {
      vatApplicable: 'false',
      defaultTaxRate: '0',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'GB',
    alpha3Code: 'GBR',
    numericCode: '826',
    commonName: 'United Kingdom',
    officialName: 'United Kingdom of Great Britain and Northern Ireland',
    officialCurrency: 'GBP',
    phoneCode: '+44',
    region: 'Europe',
    subRegion: 'Northern Europe',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '20',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'LK',
    alpha3Code: 'LKA',
    numericCode: '144',
    commonName: 'Sri Lanka',
    officialName: 'Democratic Socialist Republic of Sri Lanka',
    officialCurrency: 'LKR',
    phoneCode: '+94',
    region: 'Asia',
    subRegion: 'Southern Asia',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '15',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'DE',
    alpha3Code: 'DEU',
    numericCode: '276',
    commonName: 'Germany',
    officialName: 'Federal Republic of Germany',
    officialCurrency: 'EUR',
    phoneCode: '+49',
    region: 'Europe',
    subRegion: 'Western Europe',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '19',
      euMember: 'true'
    }
  }, {
    alpha2Code: 'FR',
    alpha3Code: 'FRA',
    numericCode: '250',
    commonName: 'France',
    officialName: 'French Republic',
    officialCurrency: 'EUR',
    phoneCode: '+33',
    region: 'Europe',
    subRegion: 'Western Europe',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '20',
      euMember: 'true'
    }
  }, {
    alpha2Code: 'AU',
    alpha3Code: 'AUS',
    numericCode: '036',
    commonName: 'Australia',
    officialName: 'Commonwealth of Australia',
    officialCurrency: 'AUD',
    phoneCode: '+61',
    region: 'Oceania',
    subRegion: 'Australia and New Zealand',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '10',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'CA',
    alpha3Code: 'CAN',
    numericCode: '124',
    commonName: 'Canada',
    officialName: 'Canada',
    officialCurrency: 'CAD',
    phoneCode: '+1',
    region: 'Americas',
    subRegion: 'Northern America',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '5',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'JP',
    alpha3Code: 'JPN',
    numericCode: '392',
    commonName: 'Japan',
    officialName: 'Japan',
    officialCurrency: 'JPY',
    phoneCode: '+81',
    region: 'Asia',
    subRegion: 'Eastern Asia',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '10',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'SG',
    alpha3Code: 'SGP',
    numericCode: '702',
    commonName: 'Singapore',
    officialName: 'Republic of Singapore',
    officialCurrency: 'SGD',
    phoneCode: '+65',
    region: 'Asia',
    subRegion: 'South-Eastern Asia',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '7',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'IN',
    alpha3Code: 'IND',
    numericCode: '356',
    commonName: 'India',
    officialName: 'Republic of India',
    officialCurrency: 'INR',
    phoneCode: '+91',
    region: 'Asia',
    subRegion: 'Southern Asia',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '18',
      euMember: 'false'
    }
  }, {
    alpha2Code: 'BR',
    alpha3Code: 'BRA',
    numericCode: '076',
    commonName: 'Brazil',
    officialName: 'Federative Republic of Brazil',
    officialCurrency: 'BRL',
    phoneCode: '+55',
    region: 'Americas',
    subRegion: 'South America',
    customFields: {
      vatApplicable: 'true',
      defaultTaxRate: '17',
      euMember: 'false'
    }
  }]);
  // Filter countries based on search query
  const filteredCountries = countries.filter(country => {
    const query = searchQuery.toLowerCase();
    const commonName = country.commonName || country.name || '';
    const officialName = country.officialName || '';
    return commonName.toLowerCase().includes(query) || officialName.toLowerCase().includes(query) || country.alpha2Code.toLowerCase().includes(query) || country.alpha3Code.toLowerCase().includes(query) || country.region.toLowerCase().includes(query) || country.subRegion.toLowerCase().includes(query) || country.officialCurrency.toLowerCase().includes(query);
  });
  const handleSelectCountry = country => {
    setSelectedCountry(country);
    setIsCreating(false);
  };
  const handleCreateCountry = () => {
    setSelectedCountry(null);
    setIsCreating(true);
  };
  const handleSaveCountry = countryData => {
    if (isCreating) {
      // Add new country
      setCountries([...countries, countryData]);
    } else {
      // Update existing country
      setCountries(countries.map(country => country.alpha2Code === countryData.alpha2Code ? countryData : country));
    }
    setSelectedCountry(null);
    setIsCreating(false);
  };
  const handleCancelEdit = () => {
    setSelectedCountry(null);
    setIsCreating(false);
  };
  const handleDeleteClick = country => {
    setCountryToDelete(country);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    setCountries(countries.filter(country => country.alpha2Code !== countryToDelete.alpha2Code));
    setShowDeleteConfirm(false);
    setCountryToDelete(null);
    if (selectedCountry && selectedCountry.alpha2Code === countryToDelete.alpha2Code) {
      setSelectedCountry(null);
    }
  };
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCountryToDelete(null);
  };
  // Add handler for breadcrumb navigation
  const handleBreadcrumbClick = level => {
    if (level === 'settings') {
      // In a real app, this would navigate to settings page
      // For now, we'll just go back to country list
      setSelectedCountry(null);
      setIsCreating(false);
    } else if (level === 'countries') {
      setSelectedCountry(null);
      setIsCreating(false);
    }
  };
  return <div className="space-y-6" data-id="element-15493">
      <div className="mb-8" data-id="element-15494">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-15495">Countries</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15496">
          Manage country configurations and metadata used across the system
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-15497">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-15498">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-15499">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-15500"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-15501">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-15502">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-15503">
                <div className="sm:flex sm:items-start" data-id="element-15504">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-15505">
                    <XIcon className="h-6 w-6 text-red-600" data-id="element-15506" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-15507">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-15508">
                      Delete Country
                    </h3>
                    <div className="mt-2" data-id="element-15509">
                      <p className="text-sm text-gray-500" data-id="element-15510">
                        Are you sure you want to delete{' '}
                        <strong data-id="element-15511">
                          {countryToDelete?.commonName || countryToDelete?.name}{' '}
                          ({countryToDelete?.alpha2Code})
                        </strong>
                        ? This action cannot be undone, and may affect any
                        records that reference this country.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15512">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={confirmDelete} data-id="element-15513">
                  Delete
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={cancelDelete} data-id="element-15514">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Country Detail or List */}
      {selectedCountry || isCreating ? <CountryDetail country={selectedCountry} onSave={handleSaveCountry} onCancel={handleCancelEdit} isCreating={isCreating} locale={locale} data-id="element-15515" /> : <>
          <div className="flex justify-between items-center mb-4" data-id="element-15516">
            <div className="relative" data-id="element-15517">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-15518">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-15519" />
              </div>
              <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md" placeholder="Search countries..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-15520" />
            </div>
            <button type="button" onClick={handleCreateCountry} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15521">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-15522" />
              Add Country
            </button>
          </div>
          <CountryList countries={filteredCountries} onSelectCountry={handleSelectCountry} onDeleteCountry={handleDeleteClick} locale={locale} data-id="element-15523" />
        </>}
    </div>;
}